import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

export type ProgramGroupStatus = 'active' | 'inactive' | 'archived'

export type ProgramGroupsListParams = {
  orgProfileId?: string
  orgProfileIds?: string | string[]
  startDate?: string
  endDate?: string
  status?: ProgramGroupStatus
}

export type CreateProgramGroupRequest = {
  name: string
  msDisplayName: string
  orgProfileId: string | null
  description: string
  iconType: string
  performanceWeight?: number
  performanceGoal?: number
  includeInMetrics?: boolean
}

export type UpdateProgramGroupRequest = {
  name?: string
  msDisplayName?: string
  orgProfileId?: string
  description?: string
  iconType?: string
  performanceWeight?: number
  performanceGoal?: number
  includeInMetrics?: boolean
}

export type UpdateProgramGroupProgramMetricsRequest = {
  includeInMetrics: boolean
}

type CreateProgramGroupResponse = {
  programGroupId: string
}

export type ProgramGroupOrganization = {
  id: string
  name: string
  icon: string
  active: boolean
}

export type ProgramGroup = {
  id: string
  name: string
  msDisplayName: string | null
  description: string | null
  iconType: string | null
  includeInMetrics: boolean
  numberOfInstances: number
  numberOfActiveInstances: number
  status: ProgramGroupStatus
  organization: ProgramGroupOrganization | null
}

type ProgramGroupsListResponse = {
  groups: ProgramGroup[]
}

export type ProgramGroupInstanceType = 'live' | 'recycler'

export type ProgramGroupInstanceCorrectResponseMetric = {
  weekStart: string
  correctCount: number
  totalCount: number
  percentage: number | null
}

export type ProgramGroupInstanceCorrectResponseSummaryMetric = {
  correctCount: number
  totalCount: number
  percentage: number | null
}

export type ProgramGroupInstanceMetrics = {
  correctResponseRate: ProgramGroupInstanceCorrectResponseMetric[]
  monthlyCorrectResponseRate: ProgramGroupInstanceCorrectResponseSummaryMetric
}

export type ProgramGroupInstanceProgram = {
  id: string
  code: string
  instanceType: ProgramGroupInstanceType
  metrics?: ProgramGroupInstanceMetrics
}

export type ProgramGroupInstance = {
  name: string
  description: string | null
  internalNotes: string | null
  includeInMetrics: boolean
  isActive: boolean
  apiVersion: number
  dateCreated: string | null
  iconType: string | null
  iconColor: string | null
  liveInstance: ProgramGroupInstanceProgram
  recyclerInstance: ProgramGroupInstanceProgram | null
}

export type ProgramGroupDetail = {
  id: string
  name: string
  msDisplayName: string | null
  orgProfileId: string | null
  description: string | null
  iconType: string | null
  includeInMetrics: boolean
  performanceWeight: number | null
  performanceGoal: number | null
  organization: ProgramGroupOrganization | null
  instances: ProgramGroupInstance[]
}

export const PROGRAM_GROUPS_LIST_KEY = 'program-groups-list'
export const PROGRAM_GROUP_DETAIL_KEY = 'program-group-detail'

export function useGetProgramGroups(params: ProgramGroupsListParams = {}) {
  return useQuery({
    queryKey: [PROGRAM_GROUPS_LIST_KEY, params],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramGroupsListResponse>('/int/v3/program-groups', {
        params,
      })

      return data.groups
    },
    refetchOnWindowFocus: false,
  })
}

export function useCreateProgramGroup() {
  const queryClient = useQueryClient()

  return useMutation<CreateProgramGroupResponse, unknown, CreateProgramGroupRequest>({
    mutationFn: async (values: CreateProgramGroupRequest) => {
      const { data } = await apiClient.post<CreateProgramGroupResponse>('/int/v3/program-groups', values)
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUPS_LIST_KEY] }),
  })
}

export function useUpdateProgramGroup() {
  const queryClient = useQueryClient()

  return useMutation<
    void,
    unknown,
    {
      programGroupId: string
      values: UpdateProgramGroupRequest
    }
  >({
    mutationFn: async ({ programGroupId, values }) => {
      await apiClient.patch(`/int/v3/program-groups/${programGroupId}`, values)
    },
    onSuccess: (_data, { programGroupId }) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUPS_LIST_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUP_DETAIL_KEY, programGroupId] })
    },
  })
}

export function useDeleteProgramGroup() {
  const queryClient = useQueryClient()

  return useMutation<void, unknown, string>({
    mutationFn: async (programGroupId: string) => {
      await apiClient.delete(`/int/v3/program-groups/${programGroupId}`)
    },
    onSuccess: (_data, programGroupId) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUPS_LIST_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUP_DETAIL_KEY, programGroupId] })
    },
  })
}

export function useRestoreProgramGroup() {
  const queryClient = useQueryClient()

  return useMutation<void, unknown, string>({
    mutationFn: async (programGroupId: string) => {
      await apiClient.post(`/int/v3/program-groups/${programGroupId}/restore`)
    },
    onSuccess: (_data, programGroupId) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUPS_LIST_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUP_DETAIL_KEY, programGroupId] })
    },
  })
}

export function useUpdateProgramGroupProgramMetrics() {
  const queryClient = useQueryClient()

  return useMutation<
    void,
    unknown,
    {
      programGroupId: string
      programId: string
      values: UpdateProgramGroupProgramMetricsRequest
    }
  >({
    mutationFn: async ({ programGroupId, programId, values }) => {
      await apiClient.patch(`/int/v3/program-groups/${programGroupId}/manage/${programId}`, values)
    },
    onSuccess: (_data, { programGroupId }) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUP_DETAIL_KEY, programGroupId] })
    },
  })
}

export function useAddProgramToGroup() {
  const queryClient = useQueryClient()

  return useMutation<
    unknown,
    unknown,
    {
      programGroupId: string
      programId: string
    }
  >({
    mutationFn: async ({ programGroupId, programId }) => {
      await apiClient.post(`/int/v3/program-groups/${programGroupId}/manage/${programId}`)
    },
    onSuccess: (_data, { programGroupId }) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUPS_LIST_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUP_DETAIL_KEY, programGroupId] })
    },
  })
}

export function useGetProgramGroupById(programGroupId?: string) {
  return useQuery({
    queryKey: [PROGRAM_GROUP_DETAIL_KEY, programGroupId],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramGroupDetail>(`/int/v3/program-groups/${programGroupId}`)
      return data
    },
    enabled: Boolean(programGroupId),
    refetchOnWindowFocus: false,
  })
}
